/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  useForm,
  getSubmitButtonProps,
  RichTextEditor,
} from '@marageti/z4-lib';
import {
  Button,
  Box,
  Alert,
  AlertTitle,
  Stack,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { LoadingButton } from '@mui/lab';
import { Controller } from 'react-hook-form';
import { MessageType } from '@marageti/z4-sdk/lib/travel';

export const ACTIONS = [{
  title: 'Phone Call: Traveler Answered',
  value: 'call-answered',
  messageType: MessageType.PhoneCall,
  snackbarMessage: 'Phone call log created.',
}, {
  title: 'Phone Call: No Answer',
  value: 'call-no-answer',
  messageType: MessageType.PhoneCallUnanswered,
  snackbarMessage: 'Phone call log created.',
}, {
  title: 'Text Message Sent',
  value: 'text',
  messageType: MessageType.SMS,
  snackbarMessage: 'Text message log created.',
}];

const schema = z.object({
  action: z.string(),
  message: z.string().optional(),
});

type LogViewProps = {
  closeDialog: () => void,
  error: Error | null;
  onSubmit: (data: any) => void;
  open: boolean,
};

const LogView = ({
  closeDialog,
  error,
  onSubmit,
  open,
}: LogViewProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid },
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      action: ACTIONS[0].value,
      message: '',
    },
  });

  const onSubmitForm = async (data: any) => {
    await onSubmit(data);
    reset();
  };

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ px: 3 }}>Log Call / Text Message</DialogTitle>
      <DialogContent sx={{
        p: 0,
        m: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      >

        {error && (
          <Alert severity="error">
            <AlertTitle>There was an error submitting log</AlertTitle>
            {error?.message}
          </Alert>
        )}
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <Stack
            spacing={2}
            sx={{
              px: 3,
              pt: 1,
              pb: 2.5,
              flex: 1,
              overflowY: 'auto',
            }}
          >
            <Controller
              name="action"
              control={control}
              render={({ field }) => (
                <RadioGroup {...field}>
                  {ACTIONS.map((act) => (
                    <FormControlLabel
                      key={act.value}
                      value={act.value}
                      control={<Radio />}
                      label={act.title}
                    />
                  ))}
                </RadioGroup>
              )}
            />
            <Box>
              <Controller
                control={control}
                name="message"
                render={({ field }) => (
                  <RichTextEditor
                    editorName="Log-Editor"
                    error={!!errors.message}
                    label={watch('action') === 'text' ? 'Message Sent (Optional)' : 'Call Notes (Optional)'}
                    onChange={field.onChange}
                    sx={{
                      maxHeight: '225px',
                      minHeight: '175px',
                      flex: 1,
                    }}
                    useLinks
                    useLists
                  />
                )}
              />
              <Typography variant="caption" color="text.secondary">
                Call and Text Logs are internal only, not visible to the other party.
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={2}
            sx={(theme) => ({
              borderTop: `1px solid ${theme.palette.divider}`,
              py: 2.5,
              px: 3,
              flexShrink: 0,
            })}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              sx={{
                width: { desktop: 'fit-content' },
              }}
              {...getSubmitButtonProps(isSubmitting || !open, isValid)}
            >
              Submit
            </LoadingButton>
            <Button
              variant="outlined"
              onClick={closeDialog}
              sx={{
                width: { desktop: 'fit-content' },
              }}
            >
              Cancel
            </Button>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LogView;
